import { Moon, Sun } from "lucide-react";
import { cn } from "@ridereserv/shared/utils";
import { useTheme } from "@/hooks/useTheme";

export interface ThemeToggleProps {
  /** Extra classes merged onto the switch button. */
  className?: string;
  /**
   * Trigger tone. "default" suits light page chrome (e.g. the marketing
   * SiteHeader); "inverse" tunes the focus ring to the ink-rail tokens so the
   * focus state reads correctly against the constant-dark `bg-sidebar` of the
   * dashboard rail. The track/thumb visuals are identical in both tones —
   * mirrors {@link NotificationBell}'s `tone` prop.
   */
  tone?: "default" | "inverse";
}

/**
 * Light/dark theme switch — a sun↔moon slider bound to {@link useTheme}.
 *
 * Renders a `role="switch"` button whose `aria-checked` reflects dark mode. The
 * track holds both icons (sun at the light end, moon at the dark end); the
 * sliding thumb carries the active icon while the opposite icon stays dimmed in
 * the track. Keyboard-operable (native `<button>`), screen-reader labelled, and
 * `prefers-reduced-motion`-safe (slide/colour transitions are gated behind
 * `motion-safe:`).
 *
 * Tap target: 44px coarse floor (`min-h-11` / `min-w-11`), densified for fine
 * pointers via `pointer-fine:`. Design-system tokens only — no hardcoded colors.
 *
 * @param root0 - ThemeToggle props
 * @param root0.className - Extra classes merged onto the switch button
 * @param root0.tone - Focus-ring tone: "default" (light chrome) or "inverse" (ink rail)
 * @returns The theme switch button
 */
export function ThemeToggle({
  className,
  tone = "default",
}: ThemeToggleProps): React.JSX.Element {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label="Toggle dark mode"
      onClick={toggleTheme}
      className={cn(
        // Coarse 44px floor, densified for fine pointers; centers the track.
        "group relative inline-flex min-h-11 min-w-11 items-center justify-center",
        "pointer-fine:min-h-9 pointer-fine:min-w-9",
        "rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        // Focus ring tuned per surface: page background vs constant-dark ink rail.
        tone === "inverse"
          ? "focus-visible:ring-sidebar-accent focus-visible:ring-offset-sidebar"
          : "focus-visible:ring-primary-500 focus-visible:ring-offset-background",
        className
      )}
    >
      {/* Track: hairline pill, flat (no in-flow shadow). */}
      <span
        aria-hidden="true"
        className={cn(
          "relative flex h-6 w-11 items-center rounded-full border border-neutral-300 bg-neutral-100",
          "transition-colors motion-reduce:transition-none",
          isDark && "border-primary-600 bg-primary-600"
        )}
      >
        {/* Sun glyph — pinned to the light (left) end of the track. */}
        <Sun
          aria-hidden="true"
          strokeWidth={1.75}
          className={cn(
            "absolute left-1 h-3.5 w-3.5 transition-opacity motion-reduce:transition-none",
            isDark ? "text-primary-100 opacity-40" : "opacity-0"
          )}
        />
        {/* Moon glyph — pinned to the dark (right) end of the track. */}
        <Moon
          aria-hidden="true"
          strokeWidth={1.75}
          className={cn(
            "absolute right-1 h-3.5 w-3.5 transition-opacity motion-reduce:transition-none",
            isDark ? "opacity-0" : "text-neutral-400 opacity-60"
          )}
        />
        {/* Thumb: slides between ends, carrying the active icon. */}
        <span
          className={cn(
            "absolute flex h-5 w-5 items-center justify-center rounded-full bg-surface",
            "transition-transform duration-200 ease-out motion-reduce:transition-none",
            isDark ? "translate-x-[1.375rem]" : "translate-x-0.5"
          )}
        >
          {isDark ? (
            <Moon
              aria-hidden="true"
              strokeWidth={1.75}
              className="h-3.5 w-3.5 text-primary-600"
            />
          ) : (
            <Sun
              aria-hidden="true"
              strokeWidth={1.75}
              className="h-3.5 w-3.5 text-warning-600"
            />
          )}
        </span>
      </span>
    </button>
  );
}
